import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiExchangeCode, apiResetPassword } from "../services/authService";
import { isValidPassword } from "../utils/validation";
import "../App.css";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [accessToken, setAccessToken] = useState(null);
  const [exchangeError, setExchangeError] = useState(null);
  const [exchanging, setExchanging] = useState(true);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Handle both PKCE (?code=) and implicit (#access_token=) flows from Supabase
  useEffect(() => {
    const code = searchParams.get("code");

    if (code) {
      // PKCE flow — exchange code for access token
      apiExchangeCode(code)
        .then(({ access_token }) => setAccessToken(access_token))
        .catch((err) => setExchangeError(err.message))
        .finally(() => setExchanging(false));
      return;
    }

    // Implicit flow — token arrives in the URL hash fragment
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const token = hashParams.get("access_token");
    const type = hashParams.get("type");

    if (token && type === "recovery") {
      setAccessToken(token);
      setExchanging(false);
      return;
    }

    setExchangeError("No reset code found. Please request a new password reset link.");
    setExchanging(false);
  }, [searchParams]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!isValidPassword(password)) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await apiResetPassword(accessToken, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="arena-bg">
      <div className="auth-modal" style={{ margin: "4rem auto", position: "relative" }}>
        {exchanging && (
          <div className="auth-pending">
            <div className="spinner" />
            <p className="auth-pending-text">Verifying reset link…</p>
          </div>
        )}

        {!exchanging && exchangeError && (
          <div className="auth-pending">
            <div className="auth-pending-icon">⚠</div>
            <h2 className="auth-title">Link Invalid</h2>
            <p className="auth-pending-text">{exchangeError}</p>
            <button className="auth-toggle-link" onClick={() => navigate("/")}>
              Back to Home
            </button>
          </div>
        )}

        {!exchanging && !exchangeError && !success && (
          <>
            <h2 className="auth-title">Choose New Password</h2>
            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              <div className="auth-field">
                <label className="auth-label" htmlFor="rp-password">New Password</label>
                <input
                  id="rp-password"
                  className="auth-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  autoFocus
                />
              </div>
              <div className="auth-field">
                <label className="auth-label" htmlFor="rp-confirm">Confirm Password</label>
                <input
                  id="rp-confirm"
                  className="auth-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              {error && <p className="auth-error">{error}</p>}
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? "Updating…" : "Update Password"}
              </button>
            </form>
          </>
        )}

        {success && (
          <div className="auth-pending">
            <div className="auth-pending-icon">✓</div>
            <h2 className="auth-title">Password Updated</h2>
            <p className="auth-pending-text">
              Your password has been changed.
            </p>
            <button
              className="auth-toggle-link"
              onClick={() => {
                window.dispatchEvent(new CustomEvent("auth:open"));
                navigate("/");
              }}
            >
              Sign In
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
